import { useEffect, useState } from 'react'
import { admin } from '../api/client'
import type { AdminStation, IssuedPushLogin, PushLogin } from '../api/types'
import { howToEnter, loginStatus } from '../lib/pushLogins'

interface Props {
  stations: AdminStation[] | undefined
}

/** Per-logger push logins (intake v2 phase 3). A secret is shown once, the
 *  moment it is issued, and is never fetchable again: only its hash is kept. */
export function PushLogins({ stations }: Props) {
  const [code, setCode] = useState('')
  const [logins, setLogins] = useState<PushLogin[]>([])
  const [issued, setIssued] = useState<IssuedPushLogin>()
  const [error, setError] = useState<string>()

  const load = (station: string) => {
    if (!station) { setLogins([]); return }
    admin.pushLogins(station).then(setLogins, (e) => setError(String(e)))
  }
  useEffect(() => { setIssued(undefined); setError(undefined); load(code) }, [code])

  const issue = async (kind: 'basic' | 'path') => {
    setError(undefined)
    try {
      setIssued(await admin.issuePushLogin(code, kind))
      load(code)
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  const revoke = async (login: PushLogin) => {
    if (!window.confirm(`Revoke login ${login.id} for logger ${login.logger_id}? `
      + 'A logger still using it will be refused from its next push.')) return
    try { await admin.revokePushLogin(login.id); load(code) }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <div className="push-logins">
      <label>
        Station{' '}
        <select value={code} onChange={(e) => setCode(e.target.value)}>
          <option value="">Choose a station</option>
          {(stations ?? []).map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </label>

      {code ? (
        <>
          <table className="grid">
            <thead><tr><th>Login</th><th>Logger</th><th>Kind</th><th>Issued</th><th>State</th><th /></tr></thead>
            <tbody>
              {logins.map((l) => (
                <tr key={l.id}>
                  <td>{l.id}</td><td>{l.logger_id}</td><td>{l.kind}</td>
                  <td>{l.created_at?.slice(0, 10) ?? ''}</td><td>{loginStatus(l)}</td>
                  <td>{l.revoked_at ? null : (
                    <button type="button" className="button" onClick={() => revoke(l)}>Revoke</button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="photo-nav">
            <button type="button" className="button" onClick={() => issue('basic')}>Issue basic login</button>
            <button type="button" className="button" onClick={() => issue('path')}>Issue path login (MRL-7)</button>
          </div>
        </>
      ) : null}

      {issued ? (
        <div className="notice notice-warn">
          <p><strong>Shown once.</strong> Copy it into Commander now; it cannot be shown again.</p>
          <dl>
            {howToEnter(issued).map(([label, value]) => (
              <div key={label}><dt>{label}</dt><dd><code>{value}</code></dd></div>
            ))}
          </dl>
        </div>
      ) : null}

      {error ? <p className="muted">{error}</p> : null}
    </div>
  )
}
